import { toTitleCase } from '@dispatch/shared';
import { isRunningInJest } from '@dispatch/testing';
import { tableStyleHideSeparator, tableStyleOverrides } from '@dispatch/web/ui';
import { Button, Card, CardContent, CardHeader, Stack } from '@mui/material';
import {
  GridColumns,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

// Row dividers are the only rules unique to this table; everything else reuses
// the shared `tableStyleOverrides` / `tableStyleHideSeparator` (composed via the
// sx array below) instead of re-declaring them. Divider color follows
// palette.mode; light keeps the original MUI grey.
const rowDividerOverrides = (theme: Theme): SystemStyleObject<Theme> => ({
  '.MuiDataGrid-cell': {
    borderBottom: 'none !important',
  },
  '.MuiDataGrid-row': {
    borderBottom: `1px solid ${
      theme.palette.mode === 'dark'
        ? theme.palette.divider
        : 'rgba(224, 224, 224, 1)'
    }`,
  },
  '.MuiDataGrid-row:last-child': {
    borderBottom: 'none !important',
  },
});

export function AdminDashboardTable<T>({
  type,
  data,
  columns = [],
  buttonTitle,
  cardTitle,
  identifier,
  handleClick,
  isLoading,
}: {
  type: string;
  buttonTitle?: string;
  /**
   * Optional override for the Card header title. Default is
   * `${toTitleCase(type)}s` (works for types whose plural is the singular
   * + `s`). Pass this when the auto-plural is wrong (e.g. "categorys").
   */
  cardTitle?: string;
  columns?: GridColumns;
  data?: T[];
  handleClick?: () => void;
  identifier: string;
  isLoading?: boolean;
}) {
  const title = toTitleCase(type);

  const CustomToolbar: React.FunctionComponent<{
    setFilterButtonEl: React.Dispatch<
      React.SetStateAction<HTMLButtonElement | null>
    >;
  }> = () => (
    <GridToolbarContainer>
      <GridToolbarQuickFilter placeholder={`Search ${title}`} />
    </GridToolbarContainer>
  );

  return (
    <Card>
      <CardHeader
        title={cardTitle ?? `${title}s`}
        sx={{
          '.MuiCardHeader-title': {
            fontSize: '18px',
          },
        }}
        action={
          <Button
            onClick={() => handleClick?.()}
            variant="outlined"
            size="small"
          >
            {buttonTitle || `Add ${title}`}
          </Button>
        }
      />
      <CardContent>
        <div style={{ height: '272px' }}>
          <DataGridPro
            disableSelectionOnClick={true}
            loading={isLoading}
            disableVirtualization={isRunningInJest()}
            rows={data || []}
            getRowId={(row) => `${row[identifier]}`}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            components={{
              Toolbar: CustomToolbar,
              NoRowsOverlay: () => (
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                >
                  No {type}s
                </Stack>
              ),
              NoResultsOverlay: () => (
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                >
                  Filter Returned No {type}s
                </Stack>
              ),
            }}
            rowHeight={32}
            columns={columns}
            pageSize={5}
            pagination
            rowsPerPageOptions={[5]}
            sx={[
              tableStyleOverrides,
              tableStyleHideSeparator,
              rowDividerOverrides,
            ]}
          />
        </div>
      </CardContent>
    </Card>
  );
}
